// src/design/DesignCenterAIAssist.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';

const API_URL = process.env.REACT_APP_API_URL;
const THINK_DELAY = 3000; // ms before each scripted AI message appears

// ─── Section-specific AI scripts ─────────────────────────────────────────────
// Each section has: opening message, 4 questions with field keys + acknowledgments
const SECTION_SCRIPTS = {
  how_things_work: {
    opening: `Let's explain how something in your industry works. Help your audience understand the process — they'll remember you when they need it. It could be how a service works, how a product is made, or how to choose between options. What is the topic for today? `,
    questions: [
      { field: 'goal',     ask: `What process or system do you want to explain?`,                                    ack: (t) => `Got it — explaining "${t}." Great topic.` },
      { field: 'audience', ask: `Who is this for — homeowners, business owners, or general public?`,                  ack: (t) => `Targeting ${t}. Smart choice.` },
      { field: 'keyMessage', ask: `What's the one thing most people don't understand about this?`,                    ack: (t) => `Key insight: "${t}." That's what will hook them.` },
      { field: 'tone',     ask: `What tone — casual and simple, or detailed and technical?`,                          ack: (t) => `Tone set to ${t}. Nice.` },
    ],
  },
  how_to_tips: {
    opening: `Let's create a helpful tip or how-to for your audience. Teach them something useful they can do themselves.`,
    questions: [
      { field: 'goal',     ask: `What tip or how-to are you sharing?`,                                               ack: (t) => `Got it — "${t}." Useful stuff.` },
      { field: 'audience', ask: `Is this a DIY tip, a maintenance reminder, or a money-saving trick?`,                ack: (t) => `Nice — framing it as a ${t}.` },
      { field: 'keyMessage', ask: `What should they avoid or watch out for?`,                                         ack: (t) => `Good callout: "${t}."` },
      { field: 'tone',     ask: `What tone — friendly helper, or expert advisor?`,                                    ack: (t) => `Tone: ${t}. Perfect.` },
    ],
  },
  problems_solutions: {
    opening: `Let's highlight a common problem your customers face and show how it gets solved. This positions you as the expert.`,
    questions: [
      { field: 'goal',     ask: `What's the problem you want to address?`,                                            ack: (t) => `Problem identified: "${t}." Relatable.` },
      { field: 'audience', ask: `What causes this problem or makes it worse?`,                                         ack: (t) => `Root cause: ${t}. That adds credibility.` },
      { field: 'keyMessage', ask: `What's the solution or fix?`,                                                       ack: (t) => `Solution: "${t}." Clear and actionable.` },
      { field: 'tone',     ask: `Is there urgency — should they act now, or is it preventative?`,                      ack: (t) => `Urgency level: ${t}. Got it.` },
    ],
  },
  advice_insights: {
    opening: `Share your professional advice or an industry insight. Let your experience build trust with your audience.`,
    questions: [
      { field: 'goal',     ask: `What advice or insight are you sharing?`,                                             ack: (t) => `Great insight: "${t}."` },
      { field: 'audience', ask: `What mistake or misconception does this address?`,                                    ack: (t) => `Addressing: ${t}. People need to hear this.` },
      { field: 'keyMessage', ask: `What should they do differently based on this advice?`,                             ack: (t) => `Action step: "${t}." That's clear.` },
      { field: 'tone',     ask: `What tone — straight talk, or warm encouragement?`,                                  ack: (t) => `Tone: ${t}. Perfect fit.` },
    ],
  },
  specials_discounts: {
    opening: `Let's promote a special offer or discount. Tell me about the deal and I'll make it pop on every platform.`,
    questions: [
      { field: 'goal',     ask: `What's the offer — discount, bundle, free consultation, seasonal special?`,           ack: (t) => `Offer: "${t}." That'll get attention.` },
      { field: 'audience', ask: `Is there an expiration date or limited availability?`,                                ack: (t) => `Timing: ${t}. Good to create urgency.` },
      { field: 'keyMessage', ask: `What action should they take — call, visit, book online?`,                          ack: (t) => `CTA: "${t}." Straightforward.` },
      { field: 'tone',     ask: `What's the hook — why should they act now?`,                                          ack: (t) => `Hook: ${t}. Love it.` },
    ],
  },
  stories_experiences: {
    opening: `Let's tell a story — a customer experience, a job you're proud of, or a moment that shows what your business is about.`,
    questions: [
      { field: 'goal',     ask: `What's the story you want to share?`,                                                ack: (t) => `Great story: "${t}."` },
      { field: 'audience', ask: `Who's involved — a customer, your team, or you personally?`,                          ack: (t) => `Characters: ${t}. That makes it real.` },
      { field: 'keyMessage', ask: `What's the takeaway or lesson?`,                                                    ack: (t) => `Takeaway: "${t}." That's the heart of it.` },
      { field: 'tone',     ask: `What tone — heartfelt, proud, or fun?`,                                              ack: (t) => `Tone: ${t}. Perfect.` },
    ],
  },
  community_relations: {
    opening: `Let's show your connection to the local community. Sponsorships, events, partnerships, or just being a good neighbor.`,
    questions: [
      { field: 'goal',     ask: `What community involvement are you highlighting?`,                                    ack: (t) => `Community focus: "${t}." That's meaningful.` },
      { field: 'audience', ask: `Is this an event, sponsorship, charity, or local partnership?`,                        ack: (t) => `Type: ${t}. Got it.` },
      { field: 'keyMessage', ask: `How does this connect to your business values?`,                                     ack: (t) => `Values: "${t}." Authentic.` },
      { field: 'tone',     ask: `Do you want to invite the audience to participate or just share the news?`,            ack: (t) => `Approach: ${t}. Makes sense.` },
    ],
  },
  humor_personality: {
    opening: `Let's show the human side of your business. Something funny, relatable, or just a glimpse behind the scenes.`,
    questions: [
      { field: 'goal',     ask: `What's the funny or relatable moment you want to share?`,                             ack: (t) => `Ha — "${t}." People will love that.` },
      { field: 'audience', ask: `Is this a workplace joke, an industry meme, or a behind-the-scenes moment?`,          ack: (t) => `Style: ${t}. Fun approach.` },
      { field: 'keyMessage', ask: `Should this be lighthearted and silly, or clever and witty?`,                        ack: (t) => `Mood: "${t}." Got it.` },
      { field: 'tone',     ask: `Any inside joke or phrase your customers would recognize?`,                            ack: (t) => `${t === 'skip' || t === 'no' || t === 'none' ? 'No worries, keeping it universal.' : `Nice touch: "${t}."`}` },
    ],
  },
};

// Fallback for unknown section keys
const DEFAULT_SCRIPT = SECTION_SCRIPTS.how_things_work;

/**
 * Typing indicator — animated dots
 */
function TypingIndicator() {
  return (
    <div className="mr-auto bg-gray-100 rounded-lg px-3 py-2 flex items-center gap-1">
      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
    </div>
  );
}

/**
 * AI Assist chat — collects a brief through section-specific questions, then calls
 * the real OpenAI endpoint to generate a headline + platform captions.
 *
 * REVISION STRATEGY:
 * - ONE endpoint: /api/designcenter/ai/generate-captions
 * - First call sends { brief, category } → full generation
 * - Every subsequent call sends { existing, revision } → cheap revision
 * - cachedResultRef stores the last result and is NEVER cleared.
 */
export default function DesignCenterAIAssist({
  selected,
  activeTabLabel,
  sectionKey,
  onCaptionsGenerated,
  onBriefComplete,
  inspirationAnswer,
  inspirationDismissed,
  inspirationLoading,
  inspirationIdeas,
  onReopenInspiration,
}) {
  // Get the script for the current section
  const script = SECTION_SCRIPTS[sectionKey] || DEFAULT_SCRIPT;

  // Conversation state — reset when section changes
  const [messages, setMessages] = useState([]);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [brief, setBrief] = useState({});
  const cachedResultRef = useRef(null);
  const [input, setInput] = useState('');
  const [isThinking, setIsThinking] = useState(false);
  const endRef = useRef(null);
  const thinkTimerRef = useRef(null);
  const prevSectionRef = useRef(sectionKey);
  const inputRef = useRef(null);

  // Reset conversation when section changes
  useEffect(() => {
    if (sectionKey !== prevSectionRef.current) {
      prevSectionRef.current = sectionKey;
      if (thinkTimerRef.current) clearTimeout(thinkTimerRef.current);
      const newScript = SECTION_SCRIPTS[sectionKey] || DEFAULT_SCRIPT;
      setMessages([{
        id: crypto.randomUUID(),
        role: 'ai',
        text: newScript.opening,
      }]);
      setQuestionIndex(0);
      setBrief({});
      cachedResultRef.current = null;
      setInput('');
      setIsThinking(false);
      initialSigRef.current = null;
    }
  }, [sectionKey]);

  // Initialize on first mount
  useEffect(() => {
    if (messages.length === 0) {
      setMessages([{
        id: crypto.randomUUID(),
        role: 'ai',
        text: script.opening,
      }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (thinkTimerRef.current) clearTimeout(thinkTimerRef.current);
    };
  }, []);

  // Keep a stable "selected signature"
  const selectedSig = useMemo(() => {
    if (!selected?.filename) return '';
    return `${selected.filename}`;
  }, [selected?.filename]);

  // Track whether user has made at least one image selection (skip initial auto-select)
  const initialSigRef = useRef(null);

  // When the selected image changes, acknowledge it (but not on first load)
  useEffect(() => {
    if (!selectedSig) return;

    // First image selected = initial auto-select, just record it
    if (initialSigRef.current === null) {
      initialSigRef.current = selectedSig;
      return;
    }

    // Same image as initial = no-op
    if (selectedSig === initialSigRef.current && !cachedResultRef.current) return;

    setMessages((prev) => {
      const notice = `Image updated. Your draft stays intact.`;
      const last = prev[prev.length - 1];

      if (last?.role === 'ai' && last?.meta === 'image-change') {
        return [
          ...prev.slice(0, -1),
          { ...last, id: crypto.randomUUID(), text: notice, sig: selectedSig },
        ];
      }

      return [
        ...prev,
        { id: crypto.randomUUID(), role: 'ai', text: notice, meta: 'image-change', sig: selectedSig },
      ];
    });
  }, [selectedSig]);

  // Auto-scroll
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages.length, isThinking]);

  // Auto-focus input when AI finishes thinking
  useEffect(() => {
    if (!isThinking) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isThinking]);

  // Auto-answer first question when an inspiration idea was chosen
  const inspirationUsedRef = useRef(false);
  useEffect(() => {
    if (!inspirationAnswer || inspirationUsedRef.current) return;
    if (questionIndex !== 0 || messages.length < 1) return;
    // Wait for the opening message + first question to be visible
    const timer = setTimeout(() => {
      if (inspirationUsedRef.current) return;
      inspirationUsedRef.current = true;

      const questions = script.questions;
      const q = questions[0];
      if (!q) return;

      // Show the answer as a user message
      setMessages((prev) => [...prev, { id: crypto.randomUUID(), role: 'user', text: inspirationAnswer }]);
      setBrief((b) => ({ ...b, [q.field]: inspirationAnswer }));
      setQuestionIndex(1);

      // Show ack + next question with the normal delay
      setIsThinking(true);
      thinkTimerRef.current = setTimeout(() => {
        setMessages((prev) => [...prev, { id: crypto.randomUUID(), role: 'ai', text: q.ack(inspirationAnswer) }]);
        setIsThinking(false);

        if (questions.length > 1) {
          setTimeout(() => {
            setIsThinking(true);
            thinkTimerRef.current = setTimeout(() => {
              setMessages((prev) => [...prev, { id: crypto.randomUUID(), role: 'ai', text: questions[1].ask }]);
              setIsThinking(false);
            }, THINK_DELAY);
          }, 200);
        }
      }, THINK_DELAY);
    }, 500);
    return () => clearTimeout(timer);
  }, [inspirationAnswer, questionIndex, messages.length, script]);

  const pushMessage = useCallback((role, text, extra) => {
    setMessages((prev) => [...prev, { id: crypto.randomUUID(), role, text, ...extra }]);
  }, []);

  // Delayed scripted AI response
  const sendDelayedAI = useCallback((acknowledgment, followUp) => {
    setIsThinking(true);

    thinkTimerRef.current = setTimeout(() => {
      pushMessage('ai', acknowledgment);
      setIsThinking(false);

      if (followUp) {
        setTimeout(() => {
          setIsThinking(true);
          thinkTimerRef.current = setTimeout(() => {
            pushMessage('ai', followUp);
            setIsThinking(false);
          }, THINK_DELAY);
        }, 200);
      }
    }, THINK_DELAY);
  }, [pushMessage]);

  // Show generated results in chat
  const showResultsInChat = useCallback((data, isRevision) => {
    pushMessage('ai', isRevision ? `Updated headline:` : `Here's your headline for the image:`);

    setTimeout(() => {
      pushMessage('ai', `"${data.headline}"`, { meta: 'headline' });
    }, 300);

    setTimeout(() => {
      const captionLines = [];
      if (data.captions?.facebook) captionLines.push(`Facebook: ${data.captions.facebook}`);
      if (data.captions?.instagram) captionLines.push(`Instagram: ${data.captions.instagram}`);
      if (data.captions?.linkedin) captionLines.push(`LinkedIn: ${data.captions.linkedin}`);
      if (data.captions?.twitter) captionLines.push(`Twitter/X: ${data.captions.twitter}`);
      if (data.captions?.tiktok) captionLines.push(`TikTok: ${data.captions.tiktok}`);

      pushMessage('ai', captionLines.join('\n\n'), { meta: 'captions' });
    }, 600);

    setTimeout(() => {
      pushMessage('ai', `Want to adjust anything? Tell me what to change, or click "Preview by Social Platform" when it looks right.`);
      setIsThinking(false);
    }, 900);
  }, [pushMessage]);

  /**
   * callAI — ONE function for ALL AI calls.
   */
  const callAI = useCallback(async (payload) => {
    setIsThinking(true);

    try {
      const res = await fetch(`${API_URL}/api/designcenter/ai/generate-captions`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!res.ok || !data.ok) {
        pushMessage('ai', `Something went wrong. ${data.error || 'Please try again.'}`);
        setIsThinking(false);
        return;
      }

      const isRevision = !!payload.existing;

      // Update cache — always
      cachedResultRef.current = { headline: data.headline, captions: data.captions };

      // Pass up to parent
      if (onCaptionsGenerated) {
        onCaptionsGenerated({ headline: data.headline, captions: data.captions });
      }

      showResultsInChat(data, isRevision);

    } catch (err) {
      console.error('[DesignCenterAIAssist] callAI error:', err);
      pushMessage('ai', `Network error — couldn't reach the AI service. Please try again.`);
      setIsThinking(false);
    }
  }, [onCaptionsGenerated, pushMessage, showResultsInChat]);

  const handleSend = () => {
    const text = input.trim();
    if (!text || isThinking) return;

    pushMessage('user', text);
    setInput('');

    // ━━━ REVISION — if we have cached data, ALWAYS use cheap revision ━━━
    if (cachedResultRef.current) {
      console.log('>>> [handleSend] REVISION — sending existing + revision to server');
      pushMessage('ai', `Updating captions...`);
      callAI({
        existing: cachedResultRef.current,
        revision: text,
      });
      return;
    }

    // ━━━ Section-specific question flow ━━━
    const questions = script.questions;

    if (questionIndex < questions.length) {
      const q = questions[questionIndex];
      const nextIdx = questionIndex + 1;

      // Store the answer in the brief
      setBrief((b) => ({ ...b, [q.field]: text }));
      setQuestionIndex(nextIdx);

      if (nextIdx < questions.length) {
        // More questions — show acknowledgment + next question
        sendDelayedAI(q.ack(text), questions[nextIdx].ask);
      } else {
        // Last question answered — generate captions
        const completedBrief = { ...brief, [q.field]: text };

        // Bubble up the completed brief so the workspace can use it for landing page generation
        if (onBriefComplete) onBriefComplete(completedBrief);

        setIsThinking(true);
        thinkTimerRef.current = setTimeout(() => {
          pushMessage('ai', q.ack(text));
          setIsThinking(false);

          setTimeout(() => {
            pushMessage('ai', `Generating your headline and captions now...`);
            callAI({ brief: completedBrief, category: activeTabLabel || 'general' });
          }, 500);
        }, THINK_DELAY);
      }
      return;
    }

    // Fallback
    sendDelayedAI(
      `Noted. When you're ready, click "Preview by Social Platform."`,
      null
    );
  };

  return (
    <div className="flex flex-col h-full min-h-0">
        {/* Chat window */}
        <div className="rounded-lg border border-gray-200 bg-white overflow-hidden flex flex-col flex-1 min-h-0">
        {/* Messages (scrolls) */}
        <div className="flex-1 min-h-0 overflow-auto overscroll-contain p-2 space-y-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
            {/* Lightbulb — reopen inspiration */}
            {inspirationDismissed && (inspirationLoading || (inspirationIdeas && inspirationIdeas.length > 0)) && (
              <button
                type="button"
                onClick={onReopenInspiration}
                className="eh-design-nodrag flex items-center gap-2 w-full px-2 py-1.5 rounded-lg hover:bg-yellow-50 transition-colors"
                title="Show inspiration ideas"
              >
                <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="#00aef7" className="shrink-0">
                  <path d="M423.5-103.5Q400-127 400-160h160q0 33-23.5 56.5T480-80q-33 0-56.5-23.5ZM320-200v-80h320v80H320Zm10-120q-69-41-109.5-110T180-580q0-125 87.5-212.5T480-880q125 0 212.5 87.5T780-580q0 81-40.5 150T630-320H330Zm24-80h252q45-32 69.5-79T700-580q0-92-64-156t-156-64q-92 0-156 64t-64 156q0 54 24.5 101t69.5 79Zm126 0Z" />
                </svg>
                <span className="text-[11px] font-semibold text-[#00aef7]">Need Inspiration?</span>
              </button>
            )}
            {messages.map((m) => (
            <div
                key={m.id}
                className={`max-w-[92%] rounded-lg px-2 py-1 text-[11px] leading-snug whitespace-pre-wrap ${
                m.role === 'user'
                    ? 'ml-auto bg-[#00aef7] text-white'
                    : m.meta === 'headline'
                    ? 'mr-auto bg-yellow-50 border border-yellow-200 text-gray-900 font-semibold'
                    : m.meta === 'captions'
                    ? 'mr-auto bg-blue-50 border border-blue-200 text-gray-700'
                    : 'mr-auto bg-gray-100 text-gray-700'
                }`}
            >
                {m.text}
            </div>
            ))}
            {isThinking && <TypingIndicator />}
            <div ref={endRef} />
        </div>

        {/* Input (always pinned) */}
        <div className="shrink-0 border-t border-gray-200 p-2 flex gap-1 bg-white">
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSend();
            }}
            placeholder={isThinking ? '' : 'Type your answer…'}
            disabled={isThinking}
            className="eh-design-nodrag flex-1 min-w-0 px-2 py-1 rounded border border-gray-200 text-xs focus:outline-none focus:border-[#00aef7] disabled:bg-gray-50 disabled:text-gray-400"
          />

            <button
            type="button"
            onClick={handleSend}
            disabled={isThinking}
            className="eh-design-nodrag shrink-0 w-6 h-6 rounded-full bg-[#00aef7] hover:bg-[#0097d6] disabled:bg-gray-300 flex items-center justify-center"
            aria-label="Send"
            title="Send"
            >
            <svg
                viewBox="0 0 24 24"
                className="w-5 h-5"
                fill="none"
                stroke="white"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
            >
                <path d="M12 19V5" />
                <path d="M5 12l7-7 7 7" />
            </svg>
            </button>

        </div>
      </div>
    </div>
  );
}
